import { c as _c } from "react/compiler-runtime";
import { useState } from 'react';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { translate } from '@openclassrooms/translations';
import { usePost } from '@openclassrooms/data-connect-web/hooks';
import Callout from '@classify/react-core-components/Callout';
import Typography from '@classify/react-core-components/Typography';
import Button from '@classify/react-core-components/Button';
import Stack from '@classify/react-core-components/Stack';
import TextField from '@classify/react-core-components/TextField';
import { useCurrentUserContext } from '@openclassrooms/data-connect-web/users';
import { LinkHandler } from '@openclassrooms/routing/components';
import { useLocalisationConfig } from '@openclassrooms/localisation/LocalisationConfigProvider';
import { Translate } from '@openclassrooms/translations';
import Select from '@classify/react-core-components/Select';
import MenuItem from '@classify/react-core-components/MenuItem';
import RoundIllustration from '@classify/react-core-components/RoundIllustration';
import openclassroomsSupportSrc from '@classify/illustrations/openclassroomsSupportRound';
type ErrorMessages = {
  email?: string;
  subject?: string;
  message?: string;
};
function useSendMessage() {
  const [isProcessing, setIsProcessing] = useState(false);
  const {
    mutateAsync: postMessage
  } = usePost('/communication/user-contact-form');
  const sendMessage = async (email: string, reason: string, message: string, ownerEmail?: string) => {
    setIsProcessing(true);
    try {
      await postMessage({
        body: {
          email,
          message,
          reason,
          ...(ownerEmail ? {
            ownerEmail
          } : {})
        }
      });
    } finally {
      setIsProcessing(false);
    }
  };
  return {
    sendMessage,
    isProcessing
  };
}
function Title(t0) {
  const $ = _c(3);
  const {
    children
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      mb: 2
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== children) {
    t2 = <Typography variant="h5" component="h1" sx={t1}>{children}</Typography>;
    $[1] = children;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  return t2;
}
function PageEmailSuccess(t0) {
  const $ = _c(21);
  const {
    onGoBack,
    hasOwner
  } = t0;
  const {
    getLocalisationConfig
  } = useLocalisationConfig();
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <RoundIllustration src={openclassroomsSupportSrc} size={100} alt="" />;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <Title>{translate("support.global.contact_channel.email.success.title")}</Title>;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== hasOwner) {
    t3 = hasOwner ? translate("contact.apl_owner.email.success.body") : translate("support.global.contact_channel.email.success.body");
    $[2] = hasOwner;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== t3) {
    t4 = <Callout type="success" illustrationElement={t1}>{t2}<Typography>{t3}</Typography></Callout>;
    $[4] = t3;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <OpenInNewIcon />;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== getLocalisationConfig) {
    t6 = getLocalisationConfig("faq.student.url");
    $[7] = getLocalisationConfig;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  const t7 = String(t6);
  let t8;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = translate("global.word.see_faq.action");
    $[9] = t8;
  } else {
    t8 = $[9];
  }
  let t9;
  if ($[10] !== t7) {
    t9 = <Button variant="primary" startIcon={t5} href={t7} target="_blank" component="a">{t8}</Button>;
    $[10] = t7;
    $[11] = t9;
  } else {
    t9 = $[11];
  }
  let t10;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = translate("global.word.back");
    $[12] = t10;
  } else {
    t10 = $[12];
  }
  let t11;
  if ($[13] !== onGoBack) {
    t11 = <Button onClick={onGoBack} variant="tertiary">{t10}</Button>;
    $[13] = onGoBack;
    $[14] = t11;
  } else {
    t11 = $[14];
  }
  let t12;
  if ($[15] !== t11 || $[16] !== t9) {
    t12 = <Stack direction="row" spacing={2} justifyContent="space-between">{t9}{t11}</Stack>;
    $[15] = t11;
    $[16] = t9;
    $[17] = t12;
  } else {
    t12 = $[17];
  }
  let t13;
  if ($[18] !== t12 || $[19] !== t4) {
    t13 = <Stack flexDirection="column" gap={4}>{t4}{t12}</Stack>;
    $[18] = t12;
    $[19] = t4;
    $[20] = t13;
  } else {
    t13 = $[20];
  }
  return t13;
}
export default function PageEmail({
  onGoBack,
  ownerEmail
}: {
  onGoBack: () => void;
  ownerEmail?: string;
}) {
  const [status, setStatus] = useState<'initial' | 'success' | 'error'>('initial');
  const {
    isProcessing,
    sendMessage
  } = useSendMessage();
  const [errors, setErrors] = useState<ErrorMessages>({});
  const {
    currentUser
  } = useCurrentUserContext();
  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.target as HTMLFormElement;
    const emailElement = form.email;
    const subjectElement = form.subject;
    const messageElement = form.message;
    const formErrors: ErrorMessages = {};
    if (!ownerEmail && !emailElement.checkValidity()) {
      formErrors.email = translate('global.form.email.error.invalid');
    }
    if (!ownerEmail && !subjectElement.checkValidity()) {
      formErrors.subject = translate('form.validation.is_required');
    }
    if (!messageElement.checkValidity()) {
      formErrors.message = translate('form.validation.is_required');
    }
    if (Object.keys(formErrors).length > 0) {
      setErrors(formErrors);
      return;
    }
    try {
      setStatus('initial');
      await sendMessage(ownerEmail ? currentUser?.email ?? '' : form.email.value, ownerEmail ? 'Other inquiries' : form.subject.value, form.message.value, ownerEmail);
      setStatus('success');
    } catch {
      setStatus('error');
    }
  };
  if (status === 'success') {
    return <PageEmailSuccess onGoBack={onGoBack} hasOwner={Boolean(ownerEmail)} />;
  }
  return <LinkHandler>
      <Title>
        {ownerEmail ? translate('contact.apl_owner.contact_cta.label') : translate('support.global.contact_channel.email.title')}
      </Title>

      <form onSubmit={handleSubmit} noValidate>
        <Stack spacing={4}>
          <Typography>
            <Translate content="support.global.contact_channel.email.subheading" unsafe />
          </Typography>

          <Stack flexDirection="column" gap={2}>
            {!ownerEmail && <TextField name="email" type="email" label={translate('support.global.contact_channel.email.form.email.label')} defaultValue={currentUser?.email || ''} required noAsterisk hasError={Boolean(errors.email)} errorHelperText={errors.email} disabled={isProcessing} />}

            {!ownerEmail && <Select name="subject" defaultValue="" displayEmpty required noAsterisk label={translate('support.global.contact_channel.email.form.reason.label')} hasError={Boolean(errors.subject)} errorHelperText={errors.subject} disabled={isProcessing}>
                <MenuItem disabled selected value="">
                  {translate('support.global.contact_channel.email.form.email.placeholder')}
                </MenuItem>

                <MenuItem value="Training and career opportunities">
                  {translate('support.global.contact_channel.email.form.reason.training')}
                </MenuItem>

                <MenuItem value="Funding and training fees">
                  {translate('support.global.contact_channel.email.form.reason.financing')}
                </MenuItem>

                <MenuItem value="Application and registration">
                  {translate('support.global.contact_channel.email.form.reason.inscription')}
                </MenuItem>

                <MenuItem value="Documents and admission requirements">
                  {translate('support.global.contact_channel.email.form.reason.documents_conditions')}
                </MenuItem>

                <MenuItem value="Technical issues">
                  {translate('support.global.contact_channel.email.form.reason.technical_problems')}
                </MenuItem>

                <MenuItem value="Other inquiries">
                  {translate('support.global.contact_channel.email.form.reason.autres')}
                </MenuItem>
              </Select>}

            <TextField name="message" multiline rows={6} required noAsterisk label={translate('support.global.contact_channel.email.form.message.label')} hasError={Boolean(errors.message)} errorHelperText={errors.message} placeholder={translate('support.global.contact_channel.email.form.message.placeholder')} disabled={isProcessing} />
          </Stack>

          {status === 'error' && <Callout type="error">
              <Typography>{translate('global.error.general_error')}</Typography>
            </Callout>}

          <Stack direction="row" spacing={2} justifyContent="space-between">
            <Button type="submit" variant="primary" isLoading={isProcessing}>
              {translate('global.word.send')}
            </Button>
            <Button onClick={onGoBack} variant="tertiary">
              {translate('global.word.back')}
            </Button>
          </Stack>
        </Stack>
      </form>
    </LinkHandler>;
}